<template>
	<div class="dynamic-dashboard-checklist" v-if="progress.percentage < 100 && !isOnboardingAlreadyCompleted">
		<!-- Minimized state -->
		<div class="minimized-checklist-card" :class="{ visible: minimized, hidden: !minimized }" @click="$emit('minimized-clicked')">
			<div class="minimized-content">
				<span class="minimized-title">Store setup</span>
				<span class="minimized-progress">{{ progress.completed }}/{{ progress.total }}</span>
			</div>
		</div>

		<!-- Full checklist -->
		<AppAccordionItem
			name="dynamic-dashboard-checklist"
			title="Store setup"
			class="overflow-visible dynamic-dashboard-checklist-accordion"
			:class="{ visible: !minimized, hidden: minimized }"
			:visible="true"
			:progress="progress"
		>
			<template v-slot:subtitle>
				<div class="checklist-header-progress-bar">
					<div class="checklist-header-progress-bar-fill" :style="{ width: progress.percentage + '%' }"></div>
				</div>
			</template>
			<div class="steps-container">
				<template v-for="(step, index) in steps">
					<div :key="index" :data-step-number="index + 1" :class="{ completed: isStepCompleted(index) }">
						<ChecklistStep :step_data="step" :isCompleted="isStepCompleted(step.index)" @step-clicked="stepAction(index)" />
					</div>
				</template>
			</div>
		</AppAccordionItem>
	</div>
</template>

<script>
	import { mapGetters, mapActions } from 'vuex'
	import axios from 'axios'

	import ChecklistStep from './ChecklistStep'

	// Global flag to prevent multiple completion calls across all component instances
	let isGloballyCompletingOnboarding = false

	export default {
		name: 'DynamicDashboardChecklist',
		components: {
			ChecklistStep,
		},
		props: {
			minimized: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				steps: [
					{
						index: 0,
						identifier: 'profile-image',
						heading: this.$t('Add profile photo'),
					},
					{
						index: 1,
						identifier: 'store-theme',
						heading: this.$t('Choose store theme'),
					},
					{
						index: 2,
						identifier: 'social-links',
						heading: this.$t('Connect socials'),
					},
					{
						index: 3,
						identifier: 'first-product',
						heading: this.$t('Create your first product'),
					},
					{
						index: 4,
						identifier: 'payment-info',
						heading: this.$t('Add payment info'),
					},
					{
						index: 5,
						identifier: 'marketing-post',
						heading: this.$t('Create marketing post'),
					},
				],
			}
		},
		computed: {
			...mapGetters('Auth', ['user']),
			...mapGetters('Stores', ['getStoreId', 'getStorePublishedPages']),
			...mapGetters('Chat', ['isChatOverlayOpen']),
			progress() {
				const completed = this.steps.filter(step => this.isStepCompleted(step.index)).length
				const total = this.steps.length
				const percentage = Math.max((completed / total) * 100, 5) // at least 5%
				return { completed, total, percentage }
			},
			isOnboardingAlreadyCompleted() {
				return this.user['onboarding-dashboard']?.status === 'completed'
			},
			isStoreThemeChosen() {
				return this.user['onboarding-dashboard']?.store_theme_chosen
			},
			isPaymentInfoAdded() {
				return this.user.integrations['stripe-connect'] || this.user.integrations.paypal
			},
			isProfileImageUploaded() {
				return this.user['onboarding-dashboard']?.profile_image_uploaded
			},
			isSocialLinksAdded() {
				return this.user['onboarding-dashboard']?.social_links_added
			},
			isFirstProductAdded() {
				return this.user['onboarding-dashboard']?.first_product_added
			},
			isMarketingPostCreated() {
				return this.user['onboarding-dashboard']?.marketing_post_created
			},
			isOnboardingCompleted() {
				return this.steps.every(step => this.isStepCompleted(step.index))
			},
			isMobile() {
				return this.$commonHelper.isMobileWidthOrLess()
			},
		},
		watch: {
			isOnboardingCompleted: {
				handler(completed) {
					if (completed) {
						this.completeOnboarding()
					}
				},
				immediate: true,
			},
		},
		methods: {
			...mapActions('Chat', ['handleCreateMarketingPost', 'closeChatOverlay']),
			...mapActions('DynamicDashboard', ['handleStepComplete']),
			async completeOnboarding() {
				// Prevent multiple calls across all component instances
				if (isGloballyCompletingOnboarding) return

				isGloballyCompletingOnboarding = true
				try {
					await axios.post('/v1/users/onboarding_dashboard_completed', {})
				} finally {
					isGloballyCompletingOnboarding = false
				}
			},
			isStepCompleted(index) {
				const step = this.steps[index]
				switch (step.identifier) {
					case 'first-product':
						return this.isFirstProductAdded || sessionStorage.getItem('onboarding-dashboard.step.first-product.ready-for-next') === '1'
					case 'store-theme':
						return this.isStoreThemeChosen || sessionStorage.getItem('onboarding-dashboard.step.store-theme.ready-for-next') === '1'
					case 'payment-info':
						return this.isPaymentInfoAdded || sessionStorage.getItem('onboarding-dashboard.step.payment-info.ready-for-next') === '1'
					case 'profile-image':
						return this.isProfileImageUploaded || sessionStorage.getItem('onboarding-dashboard.step.profile-image.ready-for-next') === '1'
					case 'social-links':
						return this.isSocialLinksAdded || sessionStorage.getItem('onboarding-dashboard.step.social-links.ready-for-next') === '1'
					case 'marketing-post':
						return (
							this.isMarketingPostCreated || sessionStorage.getItem('onboarding-dashboard.step.marketing-post.ready-for-next') === '1'
						)
					default:
						return false
				}
			},
			stepAction(index) {
				const step = this.steps.find(step => step.index === index)
				this.$stanAnalytics('dynamic-dashboard-step-click', {
					meta: { user_id: this.user.user_id, username: this.user.username },
					props: { step: step.identifier },
				})

				// Close chat overlay on mobile if it's open
				if (this.isMobile && this.isChatOverlayOpen) {
					this.closeChatOverlay()
				}

				if (step.identifier === 'first-product') {
					this.handleFirstProductClicked()
				}

				if (step.identifier === 'store-theme') {
					this.handleStoreThemeClicked()
				}

				if (step.identifier === 'payment-info') {
					this.handlePaymentInfoClicked()
				}

				if (step.identifier === 'profile-image') {
					this.handleProfileImageClicked()
				}

				if (step.identifier === 'social-links') {
					this.handleSocialLinksClicked()
				}

				if (step.identifier === 'marketing-post') {
					this.handleMarketingPostClicked()
				}
			},
			// Methods moved from Home.vue
			async handleProfileImageClicked() {
				// Navigate to profile step
				this.handleStepComplete({ action: 'go-to-profile' })
				// Route back to dashboard if not already there
				if (this.$route.path !== '/dashboard/') {
					await this.$router.push('/dashboard')
				}
			},
			async handleSocialLinksClicked() {
				// Navigate to socials step
				this.handleStepComplete({ action: 'go-to-socials' })
				// Route back to dashboard if not already there
				if (this.$route.path !== '/dashboard/') {
					await this.$router.push('/dashboard')
				}
			},
			async handleFirstProductClicked() {
				// Navigate to product step
				this.handleStepComplete({ action: 'go-to-product' })
				// Route back to dashboard if not already there
				if (this.$route.path !== '/dashboard/') {
					await this.$router.push('/dashboard')
				}
			},
			async handleStoreThemeClicked() {
				// Navigate to store theme step
				// Check if design store step is already completed
				const isDesignStoreCompleted = sessionStorage.getItem('onboarding-dashboard.step.design-store.ready-for-next') === '1'

				if (isDesignStoreCompleted) {
					// If already completed, go to design store
					await this.$router.push({
						name: 'linksites',
						query: { tab: 'design' },
					})
				} else {
					// If not completed, go to Store.vue (next step)
					this.handleStepComplete({ action: 'go-to-store' })
					// Route back to dashboard if not already there
					if (this.$route.path !== '/dashboard/') {
						await this.$router.push('/dashboard')
					}
				}
			},
			async handlePaymentInfoClicked() {
				this.$router.push({ name: 'account_settings', query: { tab: 'store' } })
			},
			async handleMarketingPostClicked() {
				// Navigate to marketing post step
				this.$stanAnalytics('chat-v2-create-marketing-post-action-clicked', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {},
				})
				await this.handleCreateMarketingPost()
			},
		},
		created() {
			if (this.isFirstProductAdded) {
				sessionStorage.setItem('onboarding-dashboard.step.first-product.ready-for-next', '1')
			}
			if (this.isStoreThemeChosen) {
				sessionStorage.setItem('onboarding-dashboard.step.store-theme.ready-for-next', '1')
			}
			if (this.isPaymentInfoAdded) {
				sessionStorage.setItem('onboarding-dashboard.step.payment-info.ready-for-next', '1')
			}
			if (this.isProfileImageUploaded) {
				sessionStorage.setItem('onboarding-dashboard.step.profile-image.ready-for-next', '1')
			}
			if (this.isSocialLinksAdded) {
				sessionStorage.setItem('onboarding-dashboard.step.social-links.ready-for-next', '1')
			}
			if (this.isMarketingPostCreated) {
				sessionStorage.setItem('onboarding-dashboard.step.marketing-post.ready-for-next', '1')
			}
		},
		mounted() {
			if (this.isMobile) {
				this.$emit('minimized-clicked')
			}
		},
	}
</script>
<style lang="scss" scoped>
	.minimized-checklist-card {
		position: absolute;
		top: 8px;
		left: 50%;
		transform: translateX(-50%);
		background: white;
		border: 1px solid #e5e7eb;
		border-radius: 8px;
		padding: 8px 12px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
		z-index: 10;
		min-width: 120px;
		transition: all 0.3s ease;
		opacity: 0;
		transform: translateX(-50%) scale(0.8);
		pointer-events: none;

		&.visible {
			opacity: 1;
			transform: translateX(-50%) scale(1);
			pointer-events: auto;
			cursor: pointer;
		}

		&.hidden {
			opacity: 0;
			transform: translateX(-50%) scale(0.8);
			pointer-events: none;
		}
	}

	.minimized-content {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
	}

	.minimized-title {
		font-family: 'Plus Jakarta Sans';
		font-size: 14px;
		font-weight: 600;
		color: #131f60;
		line-height: 1;
	}

	.minimized-progress {
		font-family: 'Inter';
		font-size: 14px;
		font-weight: 500;
		color: #6a738d;
		line-height: 1;
	}

	.dynamic-dashboard-checklist {
		transition: all 0.3s ease;
	}

	.checklist-header-progress-bar {
		width: 100%;
		height: 6px;
		background-color: #eef1f5;
		border-radius: 3px;
		margin-top: 12px;
	}
	.checklist-header-progress-bar-fill {
		height: 100%;
		background-color: #ced4ff;
		border-radius: 3px;
	}

	.steps-container {
		display: flex;
		flex-direction: column;
		gap: 20px;
		max-height: 120px;
		overflow-y: auto;
		padding-top: 8px;
		padding-bottom: 20px;

		&::-webkit-scrollbar {
			width: 6px;
		}

		&::-webkit-scrollbar-track {
			background: transparent;
		}

		&::-webkit-scrollbar-thumb {
			background: transparent;
		}

		// add margin right of -8px for non-mobile
		@media (min-width: 768px) {
			margin-right: -8px;
		}

		/* Show scrollbar on hover (desktop) */
		&:hover {
			scrollbar-color: #c1c1cc;

			@media (max-width: 508px) {
				padding-right: 0px;
			}

			&::-webkit-scrollbar-track {
				background: #f1f1f1;
				border-radius: 4px;
			}

			&::-webkit-scrollbar-thumb {
				background: #c1c1c1;
				border-radius: 4px;

				&:hover {
					background: #a8a8a8;
				}
			}
		}

		/* Show scrollbar on mobile when scrolling/touching */
		@media (max-width: 768px) {
			scrollbar-color: #c1c1c1 #f1f1f1;

			&::-webkit-scrollbar-track {
				background: #f1f1f1;
				border-radius: 4px;
			}

			&::-webkit-scrollbar-thumb {
				background: #c1c1c1;
				border-radius: 4px;
			}

			/* Show scrollbar when actively scrolling on mobile */
			&:active {
				scrollbar-color: #a8a8a8 #e0e0e0;

				&::-webkit-scrollbar-track {
					background: #e0e0e0;
				}

				&::-webkit-scrollbar-thumb {
					background: #a8a8a8;
				}
			}
		}
	}

	.dynamic-dashboard-checklist-accordion::v-deep {
		opacity: 0;
		transform: scale(0.95);
		pointer-events: none;
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;

		&.visible {
			opacity: 1;
			transform: scale(1);
			pointer-events: auto;
			position: relative;
		}

		&.hidden {
			opacity: 0;
			transform: scale(0.95);
			pointer-events: none;
			position: absolute;
		}

		.card {
			box-shadow: 0 0 1px 0 rgba(20, 20, 20, 0.12), 0 4px 12px 2px rgba(20, 20, 20, 0.08) !important;
		}

		.card-header-content-wrapper {
			padding: 12px 20px !important;
		}

		.card-body {
			padding-bottom: 2px !important;
		}

		.accordion-title-and-labels {
			justify-content: space-between;
			.title {
				color: #131f60;
				font-family: 'Plus Jakarta Sans';
				font-size: 14px !important;
				font-style: normal;
				font-weight: 600;
				line-height: 120%;
				letter-spacing: 0%;
				margin-right: 12px;
			}

			.progress-label {
				font-size: 14px !important;
				font-family: 'Inter';
				color: #6a738d;
				font-weight: 500;
				line-height: 120%;
				margin-right: 12px;
			}
		}
		.accordion-actions-wrapper {
			display: none;
		}

		.accordion-icon {
			transform: translateY(-10px) !important;

			svg path {
				fill: #6a738d;
			}
		}

		.collapsed .accordion-icon {
			transform: translateY(-10px) rotate(180deg) !important;
		}
	}
</style>
